import { defineStore } from 'pinia'
import { ref, computed, shallowRef, watch } from 'vue'
import { getAuthToken, apiClient } from '@/api/client'

interface EngraverStatus {
  engraver_id: string
  name: string
  status: string
  firmware_version: string | null
  firmware_outdated: boolean
  latest_firmware_version: string | null
  current_job_id: string | null
}

interface EngraveProgress {
  job_id: string
  completed_lines: number
  total_lines: number
  progress_pct: number
}

type EngraveResult = { type: 'complete' | 'error' | 'cancelled'; error?: string; project_id?: string }

// Sticky per-browser selection. The server keeps the durable per-user default
// (PUT /me/engravers/default); this is the fast local mirror so a reload keeps
// the same active device without a round-trip.
const SELECTED_KEY = 'keepsake_selected_engraver'

export const useEngraverStore = defineStore('engraver', () => {
  const engravers = ref<EngraverStatus[]>([])
  // Per-device job state, keyed by engraver_id. Two engravers can run different
  // jobs concurrently, so progress and results are never global.
  const progressByEngraver = ref<Record<string, EngraveProgress | null>>({})
  const resultByEngraver = ref<Record<string, EngraveResult | null>>({})
  const connected = ref(false)
  // shallowRef, not ref: several handlers below compare `ws.value !== socket`
  // to ignore a superseded socket, and that identity check must be exact. Vue
  // happens to leave a real WebSocket unproxied (reactive() skips anything
  // whose toStringTag is not Object/Array/Map/Set), so ref() would work today,
  // but relying on that is relying on an implementation detail of reactive().
  const ws = shallowRef<WebSocket | null>(null)

  // ---- Connection liveness ---------------------------------------------
  // A WebSocket that dies at the TCP layer does not reliably fire onclose.
  // Laptop sleep, a wifi change, a backgrounded phone tab, or an idle timeout
  // at the edge all leave a socket whose readyState still reads OPEN and which
  // carries nothing in either direction. Before these timers existed that was
  // indistinguishable from a quiet connection, so the page kept showing
  // whatever state last arrived and Start sent Home into a closed pipe: a
  // 45-second spinner, then "Homing timed out". Reloading the page was the only
  // recovery, and customers learned to do it instead of calling.
  //
  // So: ping on an interval, expect a pong back, and treat a missed pong as a
  // dead socket. The gateway answers `ping` with `pong` in keepsake_engraver_ws.
  const HEARTBEAT_MS = 20000
  const PONG_GRACE_MS = 10000
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null
  let pongTimer: ReturnType<typeof setTimeout> | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  const reconnectAttempt = ref(0)
  // False after an explicit disconnect()/logout, so no timer still in flight
  // can quietly reopen a socket we deliberately closed.
  const wantConnection = ref(false)

  // How many failed attempts before the badge turns red. Below this we are
  // still inside the normal cost of opening a socket (including App.vue's 2s
  // startup delay), and showing "Not synced" there would invent a problem on
  // every page load — the opposite of the point.
  const OFFLINE_AFTER_ATTEMPTS = 3

  // What the Preview page's sync badge reads. 'synced' is the only state in
  // which the engraver controls are trustworthy: we hold a socket AND its last
  // heartbeat came back.
  const syncState = computed<'synced' | 'connecting' | 'offline'>(() => {
    if (connected.value) return 'synced'
    return reconnectAttempt.value >= OFFLINE_AFTER_ATTEMPTS ? 'offline' : 'connecting'
  })

  // Machine state for the pre-engrave home/frame flow, keyed per engraver_id.
  // The gateway's home/frame ack broadcasts carry an engraver_id, so two
  // devices can be homed/framed independently. The `homed`/`framed`/... getters
  // below expose the SELECTED device's state for back-compat with PreviewView.
  interface MachineState { homed: boolean; framed: boolean; homing: boolean; framing: boolean }
  const machineByEngraver = ref<Record<string, MachineState>>({})

  function setMachine(id: string, patch: Partial<MachineState>) {
    const cur = machineByEngraver.value[id] || { homed: false, framed: false, homing: false, framing: false }
    machineByEngraver.value[id] = { ...cur, ...patch }
  }
  function machineFor(id: string | null | undefined): MachineState {
    return (id && machineByEngraver.value[id]) || { homed: false, framed: false, homing: false, framing: false }
  }

  // Transient per-engraver notice surfaced when the firmware/gateway rejects a
  // control command (e.g. "Door is open"). Cleared on the next attempt or a
  // successful home/frame so it never lingers.
  const noticeByEngraver = ref<Record<string, string | null>>({})
  function setNotice(id: string, message: string) { noticeByEngraver.value[id] = message }
  function clearNotice(id?: string | null) { if (id) noticeByEngraver.value[id] = null }

  // ---- Selection / default-engraver resolution -------------------------
  const selectedEngraverId = ref<string | null>(localStorage.getItem(SELECTED_KEY))
  // Server-resolved default (user default -> tenant default -> most-recent
  // online), supplied by GET /engravers. Used when nothing is selected locally.
  const serverDefaultEngraverId = ref<string | null>(null)

  const hasEngraver = computed(() => engravers.value.length > 0)

  const selectedEngraver = computed<EngraverStatus | null>(() =>
    engravers.value.find(e => e.engraver_id === selectedEngraverId.value)
    || engravers.value.find(e => e.engraver_id === serverDefaultEngraverId.value)
    || engravers.value[0]
    || null,
  )
  // Back-compat alias: callers that said "primaryEngraver" now mean "the
  // device this user is acting on".
  const primaryEngraver = selectedEngraver

  const isOnline = computed(() => hasEngraver.value && selectedEngraver.value?.status !== 'offline')

  // Machine state of the currently selected device (back-compat getters).
  const homed = computed(() => machineFor(selectedEngraver.value?.engraver_id).homed)
  const framed = computed(() => machineFor(selectedEngraver.value?.engraver_id).framed)
  const homing = computed(() => machineFor(selectedEngraver.value?.engraver_id).homing)
  const framing = computed(() => machineFor(selectedEngraver.value?.engraver_id).framing)
  // Notice (e.g. "Door is open") for the currently selected device.
  const notice = computed(() => {
    const id = selectedEngraver.value?.engraver_id
    return id ? (noticeByEngraver.value[id] || null) : null
  })

  function effectiveTarget(engraverId?: string): string | undefined {
    return engraverId || selectedEngraver.value?.engraver_id || undefined
  }

  function setSelectedEngraver(id: string) {
    selectedEngraverId.value = id
    localStorage.setItem(SELECTED_KEY, id)
    // Persist as this user's durable default. Best-effort: a failure here just
    // means the next device's resolution falls back to the tenant default.
    apiClient('/me/engravers/default', {
      method: 'PUT',
      body: JSON.stringify({ engraver_id: id }),
    }).catch(() => { /* non-blocking */ })
  }

  // ---- Per-device lookups (the reliable way to track one run) ----------
  // Projects are tenant-shared (keepsake_projects has no user_id), so two
  // operators routinely engrave the SAME project on two machines at once. Every
  // live signal for those runs carries the same project_id and differs only by
  // engraver_id, so anything keyed by project alone conflates them. A view that
  // knows which device its run went to (POST /engrave returns engraver_id)
  // should use these and never the per-project lookups below.
  function progressFor(engraverId?: string | null): EngraveProgress | null {
    return (engraverId && progressByEngraver.value[engraverId]) || null
  }
  function resultFor(engraverId?: string | null): EngraveResult | null {
    return (engraverId && resultByEngraver.value[engraverId]) || null
  }
  function clearResultFor(engraverId?: string | null) {
    if (engraverId) resultByEngraver.value[engraverId] = null
  }

  // ---- Per-project lookups (fallback only) -----------------------------
  // Ambiguous by construction: they return whichever device reported first when
  // two are running the same project. Only for entry points that reach a job
  // without having dispatched it and so have no engraver to pin.
  function engraverIdForProject(projectId: string): string | null {
    for (const id in progressByEngraver.value) {
      if (progressByEngraver.value[id]?.job_id === projectId) return id
    }
    return null
  }
  function progressForProject(projectId: string): EngraveProgress | null {
    const id = engraverIdForProject(projectId)
    return id ? progressByEngraver.value[id] : null
  }
  function resultForProject(projectId: string): EngraveResult | null {
    for (const id in resultByEngraver.value) {
      if (resultByEngraver.value[id]?.project_id === projectId) return resultByEngraver.value[id]
    }
    return null
  }
  function clearResultForProject(projectId: string) {
    for (const id in resultByEngraver.value) {
      if (resultByEngraver.value[id]?.project_id === projectId) resultByEngraver.value[id] = null
    }
  }

  // ---- Selected-device convenience + fleet view -------------------------
  const currentProgress = computed<EngraveProgress | null>(() => {
    const id = selectedEngraver.value?.engraver_id
    return id ? (progressByEngraver.value[id] || null) : null
  })
  const isEngraving = computed(() => currentProgress.value !== null)
  // Every device currently running a job, for a fleet-aware banner/dashboard.
  const activeEngraves = computed(() =>
    engravers.value
      .map(e => ({ engraver: e, progress: progressByEngraver.value[e.engraver_id] || null }))
      .filter((x): x is { engraver: EngraverStatus; progress: EngraveProgress } => x.progress !== null),
  )

  function clearLivenessTimers() {
    if (heartbeatTimer !== null) { clearInterval(heartbeatTimer); heartbeatTimer = null }
    if (pongTimer !== null) { clearTimeout(pongTimer); pongTimer = null }
  }

  /**
   * Drop a socket without letting its own handlers run. A socket we have
   * decided is dead must not be able to fire onclose later and schedule a
   * reconnect on top of the one we are about to open.
   */
  function teardown(socket: WebSocket | null) {
    if (!socket) return
    socket.onopen = null
    socket.onmessage = null
    socket.onclose = null
    socket.onerror = null
    try { socket.close() } catch { /* already closing */ }
  }

  function scheduleReconnect() {
    if (!wantConnection.value || reconnectTimer !== null) return
    // Back off, but stay quick: someone is standing at the engraver waiting for
    // the page to come back, so this tops out at 15s rather than a minute.
    const delay = Math.min(1000 * 2 ** reconnectAttempt.value, 15000)
    reconnectAttempt.value++
    reconnectTimer = setTimeout(() => { reconnectTimer = null; connect() }, delay)
  }

  /** Declare the current socket dead and start over. */
  function dropAndReconnect() {
    clearLivenessTimers()
    connected.value = false
    const dead = ws.value
    ws.value = null
    teardown(dead)
    scheduleReconnect()
  }

  function startHeartbeat() {
    clearLivenessTimers()
    heartbeatTimer = setInterval(() => {
      const socket = ws.value
      if (!socket || socket.readyState !== WebSocket.OPEN) { dropAndReconnect(); return }
      if (pongTimer !== null) return  // still waiting on the last one
      try {
        socket.send(JSON.stringify({ type: 'ping' }))
      } catch {
        dropAndReconnect()
        return
      }
      pongTimer = setTimeout(() => {
        pongTimer = null
        // readyState almost certainly still says OPEN here, which is the whole
        // point: this is the only signal separating a zombie socket from a
        // merely quiet one.
        dropAndReconnect()
      }, PONG_GRACE_MS)
    }, HEARTBEAT_MS)
  }

  /**
   * Re-check the connection whenever the browser hands us a reason to suspect
   * it died while we weren't watching. Coming back to a backgrounded tab is the
   * common case: phones suspend timers and drop sockets, and the page resumes
   * looking connected while holding one that carries nothing.
   */
  function revalidate() {
    if (!wantConnection.value) return
    const socket = ws.value
    if (!socket || socket.readyState === WebSocket.CLOSING || socket.readyState === WebSocket.CLOSED) {
      dropAndReconnect()
      return
    }
    if (socket.readyState !== WebSocket.OPEN) return  // still CONNECTING, let it finish
    // Looks alive. Prove it rather than trusting readyState.
    try {
      socket.send(JSON.stringify({ type: 'ping' }))
      if (pongTimer === null) {
        pongTimer = setTimeout(() => { pongTimer = null; dropAndReconnect() }, PONG_GRACE_MS)
      }
    } catch {
      dropAndReconnect()
    }
  }

  function connect() {
    wantConnection.value = true
    if (reconnectTimer !== null) { clearTimeout(reconnectTimer); reconnectTimer = null }

    // Keep only a socket that is live or still opening. The old guard was
    // `if (ws.value) return`, which also matched a CLOSING or zombie socket and
    // so turned every later reconnect into a no-op for the life of the page.
    // That is why a reload was the only way back.
    const existing = ws.value
    if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) return
    teardown(existing)
    ws.value = null

    const token = getAuthToken()
    if (!token) {
      // Auth has not landed yet (App.vue connects on a short delay after
      // fetchMe). Come back rather than leaving the store wanting a connection
      // that nothing will ever open.
      scheduleReconnect()
      return
    }

    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const host = window.location.host
    const url = `${protocol}://${host}/api/keepsake/engraver/status?token=${token}`

    const socket = new WebSocket(url)
    ws.value = socket

    socket.onopen = () => {
      if (ws.value !== socket) { teardown(socket); return }  // superseded while opening
      connected.value = true
      reconnectAttempt.value = 0
      startHeartbeat()
      // The socket was down for an unknown length of time, so every broadcast
      // sent meanwhile was missed. initial_status rebuilds the engraver list;
      // this re-reads the REST view on top of it so a reconnect can never leave
      // the page acting on pre-outage state.
      fetchEngravers()
    }

    socket.onmessage = (event) => {
      if (ws.value !== socket) return
      const data = JSON.parse(event.data)
      if (data.type === 'pong') {
        if (pongTimer !== null) { clearTimeout(pongTimer); pongTimer = null }
        return
      }
      handleMessage(data)
    }

    socket.onclose = () => {
      if (ws.value !== socket) return
      clearLivenessTimers()
      connected.value = false
      ws.value = null
      scheduleReconnect()
    }

    socket.onerror = () => {
      socket.close()
    }
  }

  // The browser's own hints that the connection may have gone while the page
  // was not running. Registered once for the life of the singleton store;
  // revalidate() is a no-op until something calls connect().
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') { revalidate(); probeTick() }
    })
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('online', revalidate)
  }

  function handleMessage(data: { type: string; [key: string]: unknown }) {
    const engraverId = data.engraver_id as string | undefined
    switch (data.type) {
      case 'engraver_probe': {
        const done = probeWaiters.get(data.probe_id as string)
        const ok = data.ok === true
        // The device just answered, so a status that says offline is stale.
        // Only ever corrected towards online here: a failed probe stays with
        // the page that asked (see MQTTBridge.probe).
        if (ok && engraverId) {
          const e = engravers.value.find(e => e.engraver_id === engraverId)
          if (e && e.status === 'offline') e.status = 'idle'
        }
        if (done) done(ok)
        break
      }

      case 'initial_status':
        engravers.value = (data.engravers as EngraverStatus[]) || []
        break

      case 'engraver_online': {
        const existing = engravers.value.find(e => e.engraver_id === data.engraver_id)
        if (existing) {
          existing.status = 'idle'
        } else {
          engravers.value.push({
            engraver_id: data.engraver_id as string,
            name: data.name as string,
            status: 'idle',
            firmware_version: null,
            firmware_outdated: false,
            latest_firmware_version: null,
            current_job_id: null,
          })
        }
        // A device that just (re)booted is no longer homed/framed.
        if (engraverId) setMachine(engraverId, { homed: false, framed: false, homing: false, framing: false })
        break
      }

      case 'engraver_offline': {
        const idx = engravers.value.findIndex(e => e.engraver_id === data.engraver_id)
        if (idx >= 0) engravers.value[idx].status = 'offline'
        if (engraverId) setMachine(engraverId, { homed: false, framed: false, homing: false, framing: false })
        break
      }

      case 'home_complete':
        if (engraverId) { setMachine(engraverId, { homing: false, homed: true }); clearNotice(engraverId) }
        break

      case 'home_error':
        if (engraverId) setMachine(engraverId, { homing: false })
        break

      case 'frame_complete':
        if (engraverId) { setMachine(engraverId, { framing: false, framed: true }); clearNotice(engraverId) }
        break

      case 'frame_error':
        if (engraverId) setMachine(engraverId, { framing: false })
        break

      case 'command_error':
        // Firmware/gateway rejected a control command (e.g. door open). Stop
        // the Home/Frame spinner and tell the user why, instead of spinning
        // forever waiting for a completion that will never come.
        if (engraverId) {
          setMachine(engraverId, { homing: false, framing: false })
          setNotice(engraverId, (data.error as string) || (data.reason as string) || 'Command failed')
        }
        break

      case 'engraver_status': {
        const e = engravers.value.find(e => e.engraver_id === data.engraver_id)
        if (e) {
          e.status = data.status as string
          if (data.firmware_version) e.firmware_version = data.firmware_version as string
        }
        break
      }

      case 'engrave_progress': {
        if (!engraverId) break
        const ep = engravers.value.find(e => e.engraver_id === engraverId)
        if (ep) ep.status = 'engraving'
        progressByEngraver.value[engraverId] = {
          job_id: (data.project_id || data.job_id) as string,
          completed_lines: data.completed_lines as number,
          total_lines: data.total_lines as number,
          progress_pct: data.progress_pct as number,
        }
        resultByEngraver.value[engraverId] = null
        break
      }

      case 'engrave_complete': {
        if (!engraverId) break
        const ec = engravers.value.find(e => e.engraver_id === engraverId)
        if (ec) ec.status = 'idle'
        resultByEngraver.value[engraverId] = { type: 'complete', project_id: data.project_id as string }
        progressByEngraver.value[engraverId] = null
        break
      }

      case 'engrave_error': {
        if (!engraverId) break
        const ee = engravers.value.find(e => e.engraver_id === engraverId)
        if (ee) ee.status = 'idle'
        resultByEngraver.value[engraverId] = {
          type: 'error',
          error: data.error as string | undefined,
          project_id: data.project_id as string,
        }
        progressByEngraver.value[engraverId] = null
        break
      }

      case 'job_cancelled': {
        const projectId = (data.project_id || data.job_id) as string | undefined
        // job_cancelled may omit engraver_id; fall back to whichever device was
        // running this project.
        const id = engraverId || (projectId ? engraverIdForProject(projectId) : null)
        if (id) {
          resultByEngraver.value[id] = { type: 'cancelled', project_id: projectId }
          progressByEngraver.value[id] = null
        }
        break
      }
    }
  }

  /**
   * Returns false when the command never left the browser. Callers that start a
   * spinner MUST check it: silently dropping the send is what produced the
   * 45-second wait and "Homing timed out" after a socket had quietly died.
   */
  function sendCommand(type: string, jobId?: string, engraverId?: string, extra?: Record<string, any>): boolean {
    const socket = ws.value
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      revalidate()
      return false
    }
    try {
      socket.send(JSON.stringify({
        type,
        job_id: jobId,
        engraver_id: engraverId,
        ...extra,
      }))
    } catch {
      dropAndReconnect()
      return false
    }
    return true
  }

  const LOST_CONNECTION = 'Lost connection to the engraver, reconnecting'

  function home(engraverId?: string) {
    const id = effectiveTarget(engraverId)
    if (!id) return
    clearNotice(id)
    setMachine(id, { homing: true, homed: false, framed: false })
    if (!sendCommand('home', undefined, id)) {
      // Nothing reached the gateway, so no completion is ever coming. Stop the
      // spinner now rather than letting homeAndWait run to its timeout.
      setMachine(id, { homing: false })
      setNotice(id, LOST_CONNECTION)
    }
  }

  function frame(shape?: string, projectId?: string, engraverId?: string) {
    const id = effectiveTarget(engraverId)
    if (!id) return
    clearNotice(id)
    setMachine(id, { framing: true, framed: false })
    if (!sendCommand('frame', projectId, id, shape ? { shape } : undefined)) {
      setMachine(id, { framing: false })
      setNotice(id, LOST_CONNECTION)
    }
  }

  /**
   * Home, and resolve once the engraver reports it is homed.
   *
   * Backs the tag "just hit Print" flow, where homing is automated instead of
   * being a button. Homing is still required even though the firmware does not
   * enforce it (handleStartJob only gates on the door): the jig anchor places
   * the design in absolute machine coordinates, and a door-open alarm from the
   * previous flip resets machine state.
   *
   * Rejects on a firmware notice ("Door is open") or timeout, so the caller can
   * surface why nothing happened rather than hanging on a dead promise.
   */
  function homeAndWait(engraverId?: string, timeoutMs = 45000): Promise<void> {
    const id = effectiveTarget(engraverId)
    if (!id) return Promise.reject(new Error('No engraver selected'))
    clearNotice(id)
    home(id)
    return new Promise((resolve, reject) => {
      const started = Date.now()
      const iv = setInterval(() => {
        if (machineFor(id).homed) { clearInterval(iv); resolve() }
        else if (noticeByEngraver.value[id]) {
          clearInterval(iv); reject(new Error(noticeByEngraver.value[id] as string))
        } else if (Date.now() - started > timeoutMs) {
          clearInterval(iv); reject(new Error('Homing timed out'))
        }
      }, 250)
    })
  }

  function startJob(projectId: string, engraverId?: string) {
    sendCommand('start_job', projectId, effectiveTarget(engraverId))
  }
  function pause(projectId: string, engraverId?: string) {
    sendCommand('pause', projectId, effectiveTarget(engraverId))
  }
  function resume(projectId: string, engraverId?: string) {
    sendCommand('resume', projectId, effectiveTarget(engraverId))
  }
  function cancel(projectId: string, engraverId?: string) {
    const id = engraverId || engraverIdForProject(projectId) || effectiveTarget()
    sendCommand('cancel', projectId, id)
    // Optimistically end the job locally — the gateway also broadcasts
    // job_cancelled for other open tabs. Without this the UI stays stuck on the
    // last progress frame until the engraver finishes homing.
    if (id) {
      resultByEngraver.value[id] = { type: 'cancelled', project_id: projectId }
      progressByEngraver.value[id] = null
    }
  }

  async function fetchEngravers() {
    try {
      const data = await apiClient<{
        engravers: any[]
        default_engraver_id?: string | null
      }>('/engravers')
      if (data.engravers) {
        engravers.value = data.engravers.map((e: any) => ({
          engraver_id: e.id,
          name: e.name,
          status: e.status || 'offline',
          firmware_version: e.firmware_version,
          firmware_outdated: !!e.firmware_outdated,
          latest_firmware_version: e.latest_firmware_version ?? null,
          current_job_id: null,
        }))
      }
      serverDefaultEngraverId.value = data.default_engraver_id ?? null
      // Heal a stale local selection (device removed, or never set).
      const ids = new Set(engravers.value.map(e => e.engraver_id))
      if (!selectedEngraverId.value || !ids.has(selectedEngraverId.value)) {
        selectedEngraverId.value = serverDefaultEngraverId.value
        if (selectedEngraverId.value) localStorage.setItem(SELECTED_KEY, selectedEngraverId.value)
        else localStorage.removeItem(SELECTED_KEY)
      }
    } catch {
      // Silently fail
    }
  }

  // ---- Engraver presence (the preview page) ---------------------------
  // `syncState` proves only that this page can reach the gateway. Whether the
  // engraver is there is a separate question, and the status we hold cannot
  // answer it: it is whatever the device last said, and an engraver that is
  // switched off goes on reading "idle" until the broker gives up on it. That
  // is how a customer was told the machine was ready while it was unplugged.
  //
  // So the preview page asks directly. While it is open it probes the selected
  // engraver every few seconds, and Start probes once more before homing. The
  // gateway pings the device over MQTT and answers within 3s. Nothing else
  // probes, so the rest of the app adds no traffic.
  const PROBE_INTERVAL_MS = 5000
  // The gateway waits 3s for the device; allow for the round trip on top.
  const PROBE_TIMEOUT_MS = 4500
  // One lost ping is a blip; two in a row is an engraver that is not there.
  const MISSES_BEFORE_DOWN = 2

  type Reachability = 'checking' | 'responding' | 'not_responding'
  const reachability = ref<Reachability>('checking')
  const probeWaiters = new Map<string, (ok: boolean) => void>()
  let probeSeq = 0
  let watchTimer: ReturnType<typeof setInterval> | null = null
  let watching = false
  let probeInFlight = false
  let probeMisses = 0

  /** Ask the gateway whether this engraver answers right now. Never rejects. */
  function probeEngraver(engraverId?: string, timeoutMs = PROBE_TIMEOUT_MS): Promise<boolean> {
    const id = effectiveTarget(engraverId)
    if (!id) return Promise.resolve(false)
    const probeId = `p${++probeSeq}`
    return new Promise(resolve => {
      const timer = setTimeout(() => finish(false), timeoutMs)
      function finish(ok: boolean) {
        clearTimeout(timer)
        probeWaiters.delete(probeId)
        resolve(ok)
      }
      probeWaiters.set(probeId, finish)
      if (!sendCommand('probe_engraver', undefined, id, { probe_id: probeId })) finish(false)
    })
  }

  async function probeTick() {
    const id = selectedEngraver.value?.engraver_id
    if (!watching || !id || probeInFlight) return
    // A dead socket is already on the badge as "Not synced". Counting it here
    // too would blame the engraver for our own connection.
    if (syncState.value !== 'synced') return
    probeInFlight = true
    const ok = await probeEngraver(id)
    probeInFlight = false
    if (!watching || selectedEngraver.value?.engraver_id !== id) return  // moved on meanwhile
    if (ok) {
      probeMisses = 0
      reachability.value = 'responding'
    } else {
      probeMisses++
      // With nothing yet to vouch for it, the first miss is enough.
      if (reachability.value === 'checking' || probeMisses >= MISSES_BEFORE_DOWN) {
        reachability.value = 'not_responding'
      }
    }
  }

  function resetReachability() {
    reachability.value = 'checking'
    probeMisses = 0
  }

  function startEngraverWatch() {
    stopEngraverWatch()
    watching = true
    resetReachability()
    watchTimer = setInterval(probeTick, PROBE_INTERVAL_MS)
    probeTick()
  }

  function stopEngraverWatch() {
    watching = false
    if (watchTimer !== null) { clearInterval(watchTimer); watchTimer = null }
  }

  // Probe at once rather than on the next tick when the answer may have
  // changed: a different engraver selected, or the socket just came back.
  watch(() => selectedEngraver.value?.engraver_id, (id, prev) => {
    if (watching && id !== prev) { resetReachability(); probeTick() }
  })
  watch(connected, (isUp) => { if (watching && isUp) probeTick() })

  function disconnect() {
    stopEngraverWatch()
    wantConnection.value = false
    if (reconnectTimer !== null) { clearTimeout(reconnectTimer); reconnectTimer = null }
    reconnectAttempt.value = 0
    clearLivenessTimers()
    connected.value = false
    const socket = ws.value
    ws.value = null
    teardown(socket)
  }

  return {
    engravers, currentProgress, connected, syncState, revalidate,
    hasEngraver, primaryEngraver, isEngraving, isOnline,
    homed, framed, homing, framing,
    notice, clearNotice,
    connect, disconnect, fetchEngravers,
    // engraver presence (preview page)
    reachability, probeEngraver, startEngraverWatch, stopEngraverWatch,
    home, homeAndWait, frame, startJob, pause, resume, cancel,
    // selection
    selectedEngraver, selectedEngraverId, serverDefaultEngraverId, setSelectedEngraver,
    // per-device job state
    progressByEngraver, resultByEngraver, activeEngraves,
    progressFor, resultFor, clearResultFor,
    progressForProject, resultForProject, clearResultForProject, engraverIdForProject,
  }
})
